import { flow, Instance, getEnv, types, cast } from "mobx-state-tree";

import { CreateCheckoutSessionInput } from "../../api-models/request/CreateCheckoutSessionInput";
import {
  SubscriptionPackageResponse,
  SubscriptionPackagesBundleResponse,
  SubscriptionPlansResponse,
} from "../../api-models/response/SubscriptionPlanResponse";
import { endpoints } from "../api/ApiEndpoints";
import { SubscriptionPlan, ExtraStoragePlan, ExtraVACallsPlan } from "./SubscriptionPlan";
import { StorageModel } from "../storage/StorageModel";
import UpdateOrganizationSubscriptionPlanInput from "../../api-models/request/UpdateOrganizationSubscriptionPlanInput";
import serializeToQueryParams from "../../helpers/QueryParams";
import GenericSubscriptionPlanInput from "../../api-models/request/GenericSubscriptionPlanInput";
import { OrganizationSubscriptionStatuses } from "../../generated/OrganizationSubscriptions";

export interface SubscriptionPlanSelection {
  planId: string;
  seatsAmount: string;
  extraStorageTBAmount: string;
  extraVACallsPackagesAmount: string;
}

const OrganizationSubscriptionPlansModel = types.model({
  subscriptionPlans: types.optional(types.array(types.frozen<SubscriptionPlan>()), []),
  currentActiveSubscription: types.optional(
    types.frozen<SubscriptionPlan>(),
    new SubscriptionPlan()
  ),
  scheduledSubscription: types.optional(types.frozen<SubscriptionPlan | undefined>(), undefined),
  subscriptionPlanIsUpdatingWithPreviousSelection: types.optional(types.boolean, false),
  extraVideoAnalysisUnitPrice: types.optional(types.number, 0),
  activeSubscriptionStatus: types.optional(types.number, OrganizationSubscriptionStatuses.Active),
  containsEnterprisePlan: types.optional(types.boolean, false),
});

export const OrganizationSubscriptionPlansStorageModel = types
  .compose(OrganizationSubscriptionPlansModel, StorageModel)
  .named("OrganizationSubscriptionPlansStorageModel")
  .actions((self) => {
    return {
      mapItem(
        item: SubscriptionPackageResponse,
        sewerInspectionModule: SubscriptionPackageResponse,
        extraStorage: SubscriptionPackageResponse,
        extraVACalls: SubscriptionPackageResponse,
        isPaidSubscription?: boolean,
        nextBillingDate?: Date,
        unsubscribeActivationDate?: Date,
        isEnterprise?: boolean,
        extraVideoAnalysisUnitCallPrice?: number
      ) {
        return new SubscriptionPlan(
          item,
          sewerInspectionModule,
          extraStorage ? new ExtraStoragePlan(extraStorage) : undefined,
          extraVACalls ? new ExtraVACallsPlan(extraVACalls) : undefined,
          isPaidSubscription ? true : false,
          nextBillingDate ? nextBillingDate : undefined,
          unsubscribeActivationDate,
          isEnterprise
        );
      },
      hasScheduledPlanDowngrade: () =>
        self.scheduledSubscription &&
        self.scheduledSubscription.id !== self.currentActiveSubscription.id,
      isNotCurrentAndScheduledPlanId: (planId: number) =>
        self.scheduledSubscription?.id !== planId && self.currentActiveSubscription.id !== planId,
    };
  })
  .actions((self) => {
    return {
      mapResponse(response: SubscriptionPlansResponse) {
        self.containsEnterprisePlan = response.availablePackagesBundle.some(
          (item) => item.sewerInspection.isEnterprise
        );

        self.subscriptionPlans = cast(
          response.availablePackagesBundle.map((item: SubscriptionPackagesBundleResponse) => {
            const plan = self.mapItem(
              item.sewerInspection,
              item.inspectionModule,
              item.extraStoragePackage,
              item.extraVACallsPackage
            );
            plan.extraVideoAnalysisUnitCallPrice = item.extraVAUnitCallPrice;
            plan.isEnterprise = item.sewerInspection.isEnterprise;
            return plan;
          })
        );

        self.currentActiveSubscription = self.mapItem(
          response.activeSubscriptionPlan.sewerInspectionPackage,
          response.activeSubscriptionPlan.sewerInspectionModule,
          response.activeSubscriptionPlan.extraStoragePackage,
          response.activeSubscriptionPlan.extraVACallsPackage,
          response.activeSubscriptionPlan.isPaidSubscription,
          response.activeSubscriptionPlan.nextBillingDate,
          response.activeSubscriptionPlan.unsubscribeActivationDate
        );

        self.scheduledSubscription = response.scheduledSubscriptionPlan
          ? self.mapItem(
              response.scheduledSubscriptionPlan.sewerInspectionPackage,
              response.scheduledSubscriptionPlan.sewerInspectionModule,
              response.scheduledSubscriptionPlan.extraStoragePackage,
              response.scheduledSubscriptionPlan.extraVACallsPackage,
              response.scheduledSubscriptionPlan.isPaidSubscription,
              response.scheduledSubscriptionPlan.nextBillingDate
            )
          : undefined;

        self.data = response;
        self.activeSubscriptionStatus = response.activeSubscriptionPlan.subscriptionStatus;
      },
      getCheckoutSessionUrl: flow(function* (requestInput: CreateCheckoutSessionInput) {
        const endpoint = endpoints.get("subscription_checkout_session");
        const sessionUrl = yield getEnv(self).api.post(endpoint, requestInput);
        return sessionUrl.data;
      }),
      updateSubscriptionPlan: flow(function* (input: UpdateOrganizationSubscriptionPlanInput) {
        const endpoint = endpoints.get("organization_subscription_update");
        yield getEnv(self).api.put(endpoint, input);
        yield self.load();
      }),
      downgradeSubscriptionPlan: flow(function* (input: GenericSubscriptionPlanInput) {
        const endpoint = endpoints.get("organization_subscription_downgrade");
        yield getEnv(self).api.put(endpoint, input);
        yield self.load();
      }),
      updateDowngradeSubscriptionPlan: flow(function* (
        input: UpdateOrganizationSubscriptionPlanInput
      ) {
        const endpoint = endpoints.get("organization_subscription_downgrade_update");
        yield getEnv(self).api.put(endpoint, input);
        yield self.load();
      }),
      checkSubscriptionPlanIsValid: flow(function* (input: GenericSubscriptionPlanInput) {
        const endpoint = endpoints.get("organization_subscription_plan_is_valid");
        return yield getEnv(self).api.post(endpoint, input);
      }),
      checkSubscriptionPlanUpdateIsValid: flow(function* (
        input: UpdateOrganizationSubscriptionPlanInput
      ) {
        const endpoint = endpoints.get("organization_subscription_update_is_valid");
        endpoint.queryParams = serializeToQueryParams({
          fullRightSeats: input.fullRightSeats,
          packageId: input.packageId,
        });
        return yield getEnv(self).api.get(endpoint);
      }),
      getCustomerPortalSessionUrl: flow(function* () {
        const endpoint = endpoints.get("customer_portal_session");
        const sessionUrl = yield getEnv(self).api.get(endpoint);

        return sessionUrl;
      }),
      updateSubscriptionPlanWithPreviousSelection: function (
        subscriptionPlanSelection: SubscriptionPlanSelection
      ) {
        self.subscriptionPlanIsUpdatingWithPreviousSelection = true;

        const selectedSubscriptionPlanId = parseInt(subscriptionPlanSelection.planId);

        const planToUpdateIndex = self.subscriptionPlans.findIndex(
          (x) => x.id === selectedSubscriptionPlanId
        );

        const updatedSubscriptionPlanWithSelection = (self.subscriptionPlans[planToUpdateIndex] = {
          ...self.subscriptionPlans[planToUpdateIndex],
          previouslySelectedAmount: parseInt(subscriptionPlanSelection.seatsAmount),
          extraStoragePackage: {
            ...self.subscriptionPlans[planToUpdateIndex].extraStoragePackage!,
            previouslySelectedAmount: parseInt(subscriptionPlanSelection.extraStorageTBAmount),
          },
          extraVACalls: {
            ...self.subscriptionPlans[planToUpdateIndex].extraVACalls!,
            previouslySelectedAmount: parseInt(
              subscriptionPlanSelection.extraVACallsPackagesAmount
            ),
          },
        });

        self.subscriptionPlans = cast(
          self.subscriptionPlans.map((subscriptionPlan) =>
            subscriptionPlan.id === selectedSubscriptionPlanId
              ? updatedSubscriptionPlanWithSelection
              : subscriptionPlan
          )
        );

        self.subscriptionPlanIsUpdatingWithPreviousSelection = false;
      },
      getExtraVideoAnalysisUnitPrice: flow(function* () {
        const endpoint = endpoints.get("extra_va_unit_price");
        var response: number = yield getEnv(self).api.get(endpoint);
        self.extraVideoAnalysisUnitPrice = response;
      }),
      upgradeSubscriptionPlan: flow(function* (input: UpdateOrganizationSubscriptionPlanInput) {
        const endpoint = endpoints.get("organization_subscription_upgrade");
        yield getEnv(self).api.put(endpoint, input);
        yield self.load();
      }),
      cancelDowngrade: flow(function* () {
        const endpoint = endpoints.get("organization_subscription_cancel_downgrade");
        yield getEnv(self).api.put(endpoint);
        yield self.load();
      }),
      cancelSubscription: flow(function* (cancelWithAutomaticDataDeletion: boolean) {
        const endpoint = endpoints.get("organization_subscription_cancel");
        yield getEnv(self).api.put(endpoint, { cancelWithAutomaticDataDeletion });
        yield self.load();
      }),
      withdrawCancelationRequest: flow(function* (planInput: GenericSubscriptionPlanInput) {
        const endpoint = endpoints.get("withdraw_subscription_cancelation_request");
        yield getEnv(self).api.put(endpoint, planInput);
        yield self.load();
      }),
      setPromoCode: flow(function* (promoCode: string) {
        const endpoint = endpoints.get("promotion");
        endpoint.queryParams = serializeToQueryParams({ promotionCode: promoCode });
        yield getEnv(self).api.put(endpoint);
      }),
      removePromoCode: flow(function* () {
        const endpoint = endpoints.get("promotion");
        yield getEnv(self).api.delete(endpoint);
      }),
      getCouponCode: flow(function* () {
        const endpoint = endpoints.get("get_subscription_coupon");
        var code: string = yield getEnv(self).api.get(endpoint);
        return code;
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("subscription_plans");
      return endpoint;
    },
    get isActiveSubscriptionPaid() {
      return self.currentActiveSubscription.isPaidSubscription;
    },
    get isLoading() {
      return (
        !self.data ||
        !self.subscriptionPlans.length ||
        !self.currentActiveSubscription ||
        self.subscriptionPlanIsUpdatingWithPreviousSelection
      );
    },
    get activeSubscriptionForNextBillingPeriod() {
      return self.scheduledSubscription ?? self.currentActiveSubscription;
    },
    get subscriptionWillBeCanceled() {
      return self.activeSubscriptionStatus === OrganizationSubscriptionStatuses.ToBeCanceled;
    },
  }));

export type OrganizationSubscriptionPlansStorage = Instance<
  typeof OrganizationSubscriptionPlansStorageModel
>;
